import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Alert,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Autocomplete
} from '@mui/material';
import { AddCircleOutline as AddCircleOutlineIcon, Close as CloseIcon } from '@mui/icons-material';
import { ref, update, get } from 'firebase/database';
import { database } from '../firebase';
import { useUser } from '../contexts/UserContext';
import { useAuth } from '../hooks/useAuth';
import { usePrograms } from '../contexts/ProgramsContext';
import { activityLogService } from '../services/activityLogService';
import { splitFullName } from '../utils/nameSplitter';
import headerBg from '../assets/header-bg.png';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`onboarding-tabpanel-${index}`}
      aria-labelledby={`onboarding-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: index === 1 ? 0.25 : 3, // 2px padding for tab 1 (Academic Information)
          pt: index === 1 ? 0.25 : 3  // Ensure top padding is also reduced
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface OnboardingModalProps {
  open: boolean;
  onClose: () => void;
  defaultTab?: number;
  redirectOnSave?: boolean;
}

interface ProfileFormData {
  userType: number; // 1=student, 2=parent, 3=guidance, 4=other
  firstName: string;
  lastName: string;
  province: string;
  highSchool: string;
  highSchoolID: string;
  grade: number; // 10, 11, or 12
  highSchoolNotListed: boolean;
  customHighSchool: string;
  selfClaimedGuidance: boolean;
}

interface CourseEntry {
  id: string;
  courseCode: string;
  gradeType: 'actual' | 'projected';
  grade: string;
}

interface AcademicFormData {
  grade12Courses: CourseEntry[];
  grade11Courses: CourseEntry[];
  grade10Courses: CourseEntry[];
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ open, onClose, defaultTab = 0, redirectOnSave = true }) => {
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useUser();
  const { user: authUser } = useAuth();
  const { calculateEligibilityForAllPrograms } = usePrograms();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highSchools, setHighSchools] = useState<Array<{id: string, name: string, city?: string}>>([]);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const [academicFormData, setAcademicFormData] = useState<AcademicFormData>({
    grade12Courses: [],
    grade11Courses: [],
    grade10Courses: []
  });
  const [courses, setCourses] = useState<Array<{code: string, name: string, grade: string}>>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [editingGradeLevel, setEditingGradeLevel] = useState<number>(12); // Track which grade level is being edited
  const [isGuidanceNoteDismissed, setIsGuidanceNoteDismissed] = useState(false); // Track if guidance note is dismissed for current user type

  // Initialize form data with existing user data or defaults
  const [formData, setFormData] = useState<ProfileFormData>({
    userType: 1, // Default to student
    firstName: '',
    lastName: '',
    province: '',
    highSchool: '',
    highSchoolID: '',
    grade: 12, // Default to Grade 12
    highSchoolNotListed: false,
    customHighSchool: '',
    selfClaimedGuidance: false
  });

  // Track previous user ID to detect actual user changes (logout or new login)
  const previousUserIdRef = useRef<string | undefined>(authUser?.uid);

  // Reset all form state when user changes (logout or new login)
  // This prevents data leakage between user sessions
  useEffect(() => {
    // Don't reset if currently saving - protect data during save operation
    if (isSaving) {
      return;
    }

    const currentUserId = authUser?.uid;
    const previousUserId = previousUserIdRef.current;

    // Reset if user changed (logout: currentUserId becomes undefined, new login: uid changes)
    if (currentUserId !== previousUserId) {
      console.log('🔄 OnboardingModal: User changed, resetting form state', {
        previousUserId,
        currentUserId
      });

      // Reset academic form data
      setAcademicFormData({
        grade12Courses: [],
        grade11Courses: [],
        grade10Courses: []
      });

      // Reset profile form data
      setFormData({
        userType: 1,
        firstName: '',
        lastName: '',
        province: '',
        highSchool: '',
        highSchoolID: '',
        grade: 12,
        highSchoolNotListed: false,
        customHighSchool: '',
        selfClaimedGuidance: false
      });

      // Reset tab to default
      setActiveTab(defaultTab);

      // Reset other state
      setEditingGradeLevel(12);
      setIsGuidanceNoteDismissed(false);
      setError(null);

      // Update ref to track current user
      previousUserIdRef.current = currentUserId;
    }
  }, [authUser?.uid, isSaving, defaultTab]);

  // Reset form state when modal closes (but not during save)
  // This ensures clean state when modal reopens
  useEffect(() => {
    if (!open && !isSaving) {
      console.log('🔄 OnboardingModal: Modal closed, resetting form state');
      
      // Reset academic form data
      setAcademicFormData({
        grade12Courses: [],
        grade11Courses: [],
        grade10Courses: []
      });

      // Reset tab to default
      setActiveTab(defaultTab);

      // Reset other state
      setEditingGradeLevel(12);
      setIsGuidanceNoteDismissed(false);
      setError(null);
    }
  }, [open, isSaving, defaultTab]);

  // Load existing user data when modal opens
  useEffect(() => {
    
    if (open && user) {
      // Calculate grade from existing classYear if available
      let calculatedGrade = 12; // Default to Grade 12
      if (user.classYear) {
        calculatedGrade = calculateGradeFromClassYear(user.classYear);
      }
      
      // Determine first and last names
      let firstName = user.firstName || '';
      let lastName = user.lastName || '';
      
      // If user doesn't have existing first/last names but has a username from signup, split it
      // This improves UX by pre-populating the form with the name they entered during signup
      if ((!firstName || !lastName) && authUser?.username) {
        const splitName = splitFullName(authUser.username);
        // Only use split names if we don't have existing names (preserve any existing data)
        if (!firstName) firstName = splitName.firstName;
        if (!lastName) lastName = splitName.lastName;
      }
      
      setFormData({
        userType: user.userType || 1, // Use existing userType or default to student
        firstName,
        lastName,
        province: user.province || '',
        highSchool: user.schoolName || '',
        highSchoolID: user.schoolID || '',
        grade: calculatedGrade,
        highSchoolNotListed: false,
        customHighSchool: '',
        selfClaimedGuidance: user.userType === 3 ? (user.selfClaimedGuidance || false) : false
      });
    }
  }, [open, user, authUser]);

  // Watch for authUser.username changes and update form when available
  useEffect(() => {
    // If modal is open, user exists, and we have a username but no firstName/lastName
    if (open && user && authUser?.username && (!formData.firstName || !formData.lastName)) {
      const splitName = splitFullName(authUser.username);
      
      setFormData(prevFormData => ({
        ...prevFormData,
        firstName: splitName.firstName || prevFormData.firstName,
        lastName: splitName.lastName || prevFormData.lastName
      }));
    }
  }, [open, user, authUser?.username, formData.firstName, formData.lastName]);

  // Load existing grades when modal opens
  useEffect(() => {
    // Don't load grades if currently saving - protect data during save operation
    if (isSaving) {
      return;
    }

    const loadExistingGrades = async () => {
      if (open && authUser?.uid) {
        try {
          const userRef = ref(database, `user/${authUser.uid}/grades`);
          const snapshot = await get(userRef);
          
          if (snapshot.exists()) {
            const existingGrades = snapshot.val();
            const grade12Courses: CourseEntry[] = [];
            const grade11Courses: CourseEntry[] = [];
            const grade10Courses: CourseEntry[] = [];

            // Convert existing grades to CourseEntry format
            Object.entries(existingGrades).forEach(([courseCode, courseData]: [string, any]) => {
              const courseEntry: CourseEntry = {
                id: courseCode,
                courseCode: courseCode,
                gradeType: courseData.gradeType || 'actual',
                grade: courseData.grades?.toString() || ''
              };

              // Find the course in our courses list to get the correct grade level
              const courseInfo = courses.find(c => c.code === courseCode);
              if (courseInfo) {
                // Use the actual grade from the course data
                switch (courseInfo.grade) {
                  case '12':
                    grade12Courses.push(courseEntry);
                    break;
                  case '11':
                    grade11Courses.push(courseEntry);
                    break;
                  case '10':
                    grade10Courses.push(courseEntry);
                    break;
                  default:
                    // Fallback to pattern matching for courses not in our list
                    if (courseCode.includes('4U') || courseCode.includes('4C') || courseCode.includes('4M')) {
                      grade12Courses.push(courseEntry);
                    } else if (courseCode.includes('3U') || courseCode.includes('3C') || courseCode.includes('3M')) {
                      grade11Courses.push(courseEntry);
                    } else if (courseCode.includes('2D') || courseCode.includes('2P')) {
                      grade10Courses.push(courseEntry);
                    }
                    break;
                }
              } else {
                // Fallback to pattern matching if course not found in our list
                if (courseCode.includes('4U') || courseCode.includes('4C') || courseCode.includes('4M')) {
                  grade12Courses.push(courseEntry);
                } else if (courseCode.includes('3U') || courseCode.includes('3C') || courseCode.includes('3M')) {
                  grade11Courses.push(courseEntry);
                } else if (courseCode.includes('2D') || courseCode.includes('2P')) {
                  grade10Courses.push(courseEntry);
                }
              }
            });

            setAcademicFormData({
              grade12Courses,
              grade11Courses,
              grade10Courses
            });
          } else {
            // If user has no grades, ensure academicFormData is cleared
            // This prevents old user's data from persisting
            setAcademicFormData({
              grade12Courses: [],
              grade11Courses: [],
              grade10Courses: []
            });
          }
        } catch (error) {
          console.error('Error loading existing grades:', error);
        }
      }
    };

    // Only load existing grades if courses are already loaded
    if (courses.length > 0) {
      loadExistingGrades();
    }
  }, [open, authUser?.uid, courses, isSaving]);

  // Initialize editing grade level when user's grade changes
  useEffect(() => {
    setEditingGradeLevel(formData.grade);
  }, [formData.grade]);

  // Reset guidance note dismissal when user type changes
  useEffect(() => {
    setActiveTab(defaultTab);
    setIsGuidanceNoteDismissed(false);
  }, [defaultTab, formData.userType]);

  // Load high schools when user is authenticated
  useEffect(() => {
    const fetchHighSchools = async () => {
      // Only fetch if user is authenticated
      if (!authUser?.uid) {
        return;
      }
      
      setLoadingSchools(true);
      try {
        const schoolsRef = ref(database, 'highSchools');
        const snapshot = await get(schoolsRef);
        if (snapshot.exists()) {
          const schoolsData = snapshot.val();
          const schoolsList = Object.entries(schoolsData).map(([id, data]: [string, any]) => ({
            id,
            name: data.HighSchoolName || data.SchoolName || '',
            city: data.City || data.SchoolCity || data.HighSchoolCity || ''
          })).filter(school => school.name) // Filter out schools without names
          .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically
          .filter((school, index, array) => {
            // Remove duplicates by keeping only the first occurrence of each school name
            return array.findIndex(s => s.name === school.name) === index;
          });
          setHighSchools(schoolsList);
        }
      } catch (error) {
        console.error('Error fetching high schools:', error);
      } finally {
        setLoadingSchools(false);
      }
    };

    fetchHighSchools();
  }, [authUser?.uid]);

  // Load courses when user is authenticated
  useEffect(() => {
    const fetchCourses = async () => {
      // Only fetch if user is authenticated
      if (!authUser?.uid) {
        return;
      }
      
      setLoadingCourses(true);
      try {
        const coursesRef = ref(database, 'highSchoolCourses_new');
        const snapshot = await get(coursesRef);
        if (snapshot.exists()) {
          const coursesData = snapshot.val();
          const allCourses: Array<{code: string, name: string, grade: string}> = [];

          // Iterate through all courses
          for (const courseCode in coursesData) {
            const courseData = coursesData[courseCode];
            const grade = courseData.Grade;
            const name = courseData.Name || '';

            if (grade === '12' || grade === '11' || grade === '10') {
              allCourses.push({ code: courseCode, name, grade });
            }
          }

          // Sort courses by code for consistency
          allCourses.sort((a, b) => a.code.localeCompare(b.code));
          setCourses(allCourses);
        }
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [authUser?.uid]);

  // Calculate user's grade based on class year (currently unused but kept for future use)
  // const calculateUserGrade = (classYear: string): number => {
  //   const currentYear = new Date().getFullYear();
  //   const currentMonth = new Date().getMonth() + 1; // January is 0, so add 1
  //   const classYearNum = parseInt(classYear);
  //   
  //   if (currentMonth >= 7) {
  //     // July or later
  //     if (classYearNum === currentYear || classYearNum === currentYear + 1) return 12;
  //     if (classYearNum === currentYear + 2) return 11;
  //     return 10; // classYearNum > currentYear + 2
  //   } else {
  //     // Before July
  //     if (classYearNum === currentYear) return 12;
  //     if (classYearNum === currentYear + 1) return 11;
  //     return 10; // classYearNum >= currentYear + 2
  //   }
  // };

  // Calculate grade from class year (reverse of calculateUserGrade)
  const calculateGradeFromClassYear = (classYear: string): number => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // January is 0, so add 1
    const classYearNum = parseInt(classYear);
    
    if (currentMonth >= 7) {
      // July or later
      if (classYearNum === currentYear || classYearNum === currentYear + 1) return 12;
      if (classYearNum === currentYear + 2) return 11;
      return 10; // classYearNum > currentYear + 2
    } else {
      // Before July
      if (classYearNum === currentYear) return 12;
      if (classYearNum === currentYear + 1) return 11;
      return 10; // classYearNum >= currentYear + 2
    }
  };

  // Calculate class year from grade
  const calculateClassYearFromGrade = (grade: number): string => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // January is 0, so add 1
    
    if (grade === 12) {
      if (currentMonth < 7) {
        return currentYear.toString();
      } else {
        return (currentYear + 1).toString();
      }
    } else if (grade === 11) {
      if (currentMonth < 7) {
        return (currentYear + 1).toString();
      } else {
        return (currentYear + 2).toString();
      }
    } else if (grade === 10) {
      if (currentMonth < 7) {
        return (currentYear + 2).toString();
      } else {
        return (currentYear + 3).toString();
      }
    }
    
    return (currentYear + 1).toString(); // Default fallback
  };

  // Get the appropriate grade courses to show based on editing grade level
  const getUserGradeCourses = () => {
    switch (editingGradeLevel) {
      case 12:
        return { grade: 12, courses: academicFormData.grade12Courses, title: 'Enter Grade 12 Courses' };
      case 11:
        return { grade: 11, courses: academicFormData.grade11Courses, title: 'Enter Grade 11 Courses' };
      case 10:
        return { grade: 10, courses: academicFormData.grade10Courses, title: 'Enter Grade 10 Courses' };
      default:
        return { grade: 12, courses: academicFormData.grade12Courses, title: 'Enter Grade 12 Courses' };
    }
  };

  // Function to determine default grade type based on user's grade and the grade level being edited
  const getDefaultGradeType = (gradeLevel: number): 'actual' | 'projected' => {
    const userGrade = formData.grade;
    
    // If user is in Grade 12:
    // - Grade 12 courses should be "projected" (currently taking them)
    // - Grade 11 courses should be "actual" (completed last year)
    // - Grade 10 courses should be "actual" (completed two years ago)
    // If user is in Grade 11:
    // - Grade 11 courses should be "projected" (currently taking them)
    // - Grade 10 courses should be "actual" (completed last year)
    // If user is in Grade 10:
    // - Grade 10 courses should be "projected" (currently taking them)
    
    if (userGrade === 12) {
      return gradeLevel === 12 ? 'projected' : 'actual';
    } else if (userGrade === 11) {
      return gradeLevel === 11 ? 'projected' : 'actual';
    } else {
      return 'projected'; // Grade 10 students
    }
  };

  // Add placeholder courses for each grade level
  const addPlaceholderCourses = (grade: number) => {
    const placeholderCourses = {
      12: [
        { code: 'ENG4U', name: 'English' },
        { code: 'MHF4U', name: 'Advanced Functions' },
        { code: 'MCV4U', name: 'Calculus and Vectors' },
        { code: 'MDM4U', name: 'Mathematics of Data Management' },
        { code: 'SCH4U', name: 'Chemistry' },
        { code: 'SBI4U', name: 'Biology' },
        { code: '', name: '' },
        { code: '', name: '' }
      ],
      11: [
        { code: 'ENG3U', name: 'English' },
        { code: 'MCR3U', name: 'Functions' },
        { code: 'SBI3U', name: 'Biology' },
        { code: 'SCH3U', name: 'Chemistry' },
        { code: 'SPH3U', name: 'Physics' },
        { code: 'HSP3U', name: 'Introduction to Anthropology, Psychology, and Sociology' },
        { code: 'CLU3M', name: 'Understanding Canadian Law' },
        { code: 'FSF3U', name: 'Core French' }
      ],
      10: [
        { code: 'ENG2D', name: 'English' },
        { code: 'MPM2D', name: 'Principles of Mathematics' },
        { code: 'SNC2D', name: 'Science' },
        { code: 'CHC2D', name: 'Canadian History Since World War I' },
        { code: 'CHV2O', name: 'Civics and Citizenship' },
        { code: 'GLC2O', name: 'Career Studies' },
        { code: 'FSF2O', name: 'Core French' },
        { code: 'BBI2O', name: 'Introduction to Business' }
      ]
    };

    const courses = placeholderCourses[grade as keyof typeof placeholderCourses] || [];
    const newCourses = courses.map((course, index) => ({
      id: `placeholder-${grade}-${index}`,
      courseCode: course.code,
      gradeType: getDefaultGradeType(grade),
      grade: ''
    }));

    setAcademicFormData(prev => {
      switch (grade) {
        case 12:
          return { ...prev, grade12Courses: newCourses };
        case 11:
          return { ...prev, grade11Courses: newCourses };
        case 10:
          return { ...prev, grade10Courses: newCourses };
        default:
          return prev;
      }
    });
  };

  // Auto-add placeholder courses when user first accesses the Academic Information tab
  useEffect(() => {
    if (activeTab === 1) {
      const currentCourses = getUserGradeCourses().courses;
      
      // Only add placeholder courses if no courses exist for this grade
      // AND if the user doesn't already have existing grades loaded
      const hasExistingGrades = academicFormData.grade12Courses.length > 0 || 
                               academicFormData.grade11Courses.length > 0 || 
                               academicFormData.grade10Courses.length > 0;
      
      if (currentCourses.length === 0 && !hasExistingGrades) {
        addPlaceholderCourses(editingGradeLevel);
      }
    }
  }, [activeTab, editingGradeLevel, academicFormData, getUserGradeCourses, addPlaceholderCourses]);

  // Add placeholder courses when switching to a grade level that has no courses
  useEffect(() => {
    if (activeTab === 1) {
      const currentCourses = getUserGradeCourses().courses;
      
      // Only add placeholder courses if no courses exist for this grade level
      // AND if the user doesn't already have existing grades loaded for this specific grade
      const hasExistingGradesForThisGrade = 
        (editingGradeLevel === 12 && academicFormData.grade12Courses.length > 0) ||
        (editingGradeLevel === 11 && academicFormData.grade11Courses.length > 0) ||
        (editingGradeLevel === 10 && academicFormData.grade10Courses.length > 0);
      
      if (currentCourses.length === 0 && !hasExistingGradesForThisGrade) {
        addPlaceholderCourses(editingGradeLevel);
      }
    }
  }, [editingGradeLevel, activeTab, academicFormData, getUserGradeCourses, addPlaceholderCourses]);

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    // If user is switching to the Academic Information tab (tab 1) from Profile Information tab (tab 0)
    if (newValue === 1 && activeTab === 0) {
      // Validate first tab
      if (!formData.userType || !formData.firstName || !formData.lastName || !formData.province || !formData.grade) {
        setError('Please fill in all required fields');
        return;
      }

      // Check high school validation
      if (!formData.highSchoolNotListed && !formData.highSchool) {
        setError('Please select a high school or check "My High School is not listed"');
        return;
      }

      if (formData.highSchoolNotListed && !formData.customHighSchool) {
        setError('Please provide your high school name');
        return;
      }

      // Validate selfClaimedGuidance for Guidance users
      if (formData.userType === 3 && !formData.selfClaimedGuidance) {
        setError('Please certify that you are in a Guidance role at the selected high school');
        return;
      }

          // Save data from first tab
          if (authUser?.uid) {
            setIsSaving(true);
            setError(null);
            
            try {
              const userRef = ref(database, `user/${authUser.uid}`);
              const calculatedClassYear = calculateClassYearFromGrade(formData.grade);
              
              // Determine the high school to save
              const highSchoolToSave = formData.highSchoolNotListed ? 'Fosterway High School' : formData.highSchool;
              const highSchoolIDToSave = formData.highSchoolNotListed ? '' : formData.highSchoolID;
              
              const updates: any = {
                'ProfileData/First Name': formData.firstName,
                'ProfileData/Last Name': formData.lastName,
                'ProfileData/Province': formData.province,
                'ProfileData/High School': highSchoolToSave,
                'ProfileData/hsID': highSchoolIDToSave,
                'ProfileData/Class Year': calculatedClassYear,
                'ProfileData/isStudent': formData.userType,
                'ProfileData/Email': authUser?.email || user?.email || '',
                'ProfileData/selfClaimedGuidance': formData.selfClaimedGuidance
              };

              await update(userRef, updates);
              
              // Log the profile update activity
          if (authUser?.uid) {
            try {
              await activityLogService.logProfileUpdate(authUser.uid);
            } catch (error) {
              console.error('Error logging profile update activity:', error);
              // Don't block the flow if logging fails
            }
          }
          
          // Switch to Academic Information tab after successful save
          setActiveTab(newValue);
          setError(null);
        } catch (err: any) {
          console.error('Error saving onboarding data:', err);
          setError('Failed to save profile information. Please try again.');
        } finally {
          setIsSaving(false);
        }
      } else {
        setError('User not authenticated');
      }
    } else {
      // For other tab changes (like going back), just switch tabs
      setActiveTab(newValue);
      setError(null);
    }
  };

  const handleInputChange = (field: keyof ProfileFormData, value: string | number | boolean) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: field === 'grade' ? value as number : value
      };
      
      // Handle checkbox logic
      if (field === 'highSchoolNotListed') {
        if (value === true) {
          // When checkbox is checked, clear the high school selection
          newData.highSchool = '';
          newData.highSchoolID = '';
        } else {
          // When checkbox is unchecked, clear the custom high school field
          newData.customHighSchool = '';
        }
      }
      
      // Handle userType changes - reset selfClaimedGuidance
      if (field === 'userType') {
        // When user type changes, reset selfClaimedGuidance to false
        // User must manually check the box to certify
        newData.selfClaimedGuidance = false;
      }
      
      return newData;
    });
  };

  const handleGradeChange = (grade: number) => {
    setFormData(prev => ({
      ...prev,
      grade: grade
    }));
  };

  // Handle high school selection
  const handleHighSchoolChange = (schoolName: string) => {
    // Find the selected school to get its ID
    const selectedSchool = highSchools.find(school => school.name === schoolName);
    
    setFormData(prev => ({
      ...prev,
      highSchool: schoolName,
      highSchoolID: selectedSchool?.id || ''
    }));
  };

  // Course management functions
  const addCourse = (grade: number) => {
    const newCourse: CourseEntry = {
      id: Date.now().toString(),
      courseCode: '',
      gradeType: getDefaultGradeType(grade),
      grade: ''
    };

    setAcademicFormData(prev => {
      switch (grade) {
        case 12:
          return { ...prev, grade12Courses: [...prev.grade12Courses, newCourse] };
        case 11:
          return { ...prev, grade11Courses: [...prev.grade11Courses, newCourse] };
        case 10:
          return { ...prev, grade10Courses: [...prev.grade10Courses, newCourse] };
        default:
          return prev;
      }
    });
  };

  const removeCourse = (grade: number, courseId: string) => {
    setAcademicFormData(prev => {
      switch (grade) {
        case 12:
          return { ...prev, grade12Courses: prev.grade12Courses.filter(course => course.id !== courseId) };
        case 11:
          return { ...prev, grade11Courses: prev.grade11Courses.filter(course => course.id !== courseId) };
        case 10:
          return { ...prev, grade10Courses: prev.grade10Courses.filter(course => course.id !== courseId) };
        default:
          return prev;
      }
    });
  };

  const updateCourse = (grade: number, courseId: string, field: keyof CourseEntry, value: string) => {
    setAcademicFormData(prev => {
      const updateCourseInArray = (courses: CourseEntry[]) =>
        courses.map(course => 
          course.id === courseId ? { ...course, [field]: value } : course
        );

      switch (grade) {
        case 12:
          return { ...prev, grade12Courses: updateCourseInArray(prev.grade12Courses) };
        case 11:
          return { ...prev, grade11Courses: updateCourseInArray(prev.grade11Courses) };
        case 10:
          return { ...prev, grade10Courses: updateCourseInArray(prev.grade10Courses) };
        default:
          return prev;
      }
    });
  };

  const handleNext = async () => {
    if (activeTab === 0) {
      // Validate first tab
      if (!formData.userType || !formData.firstName || !formData.lastName || !formData.province || !formData.grade) {
        setError('Please fill in all required fields');
        return;
      }

      // Check high school validation
      if (!formData.highSchoolNotListed && !formData.highSchool) {
        setError('Please select a high school or check "My High School is not listed"');
        return;
      }

      if (formData.highSchoolNotListed && !formData.customHighSchool) {
        setError('Please provide your high school name');
        return;
      }

      // Validate selfClaimedGuidance for Guidance users
      if (formData.userType === 3 && !formData.selfClaimedGuidance) {
        setError('Please certify that you are in a Guidance role at the selected high school');
        return;
      }

      // Save data from first tab
      if (authUser?.uid) {
        setIsSaving(true);
        setError(null);
        
        try {
          const userRef = ref(database, `user/${authUser.uid}`);
          const calculatedClassYear = calculateClassYearFromGrade(formData.grade);
          
          // Determine the high school to save
          const highSchoolToSave = formData.highSchoolNotListed ? 'Fosterway High School' : formData.highSchool;
          const highSchoolIDToSave = formData.highSchoolNotListed ? '' : formData.highSchoolID;
          
          const updates: any = {
            'ProfileData/First Name': formData.firstName,
            'ProfileData/Last Name': formData.lastName,
            'ProfileData/Province': formData.province,
            'ProfileData/High School': highSchoolToSave,
            'ProfileData/hsID': highSchoolIDToSave,
            'ProfileData/Class Year': calculatedClassYear,
            'ProfileData/isStudent': formData.userType,
            'ProfileData/selfClaimedGuidance': formData.selfClaimedGuidance
          };

          await update(userRef, updates);
          
          // Move to next tab after successful save
          setActiveTab(1);
          setError(null);
        } catch (err: any) {
          console.error('Error saving onboarding data:', err);
          setError('Failed to save profile information. Please try again.');
        } finally {
          setIsSaving(false);
        }
      } else {
        setError('User not authenticated');
      }
    }
  };

  const handleBack = () => {
    setActiveTab(0);
    setError(null);
  };

  // Function to validate that user has at least 6 courses with grades across any grade level
  const validateMinimumCourses = () => {
    const allCourses = [
      ...academicFormData.grade12Courses,
      ...academicFormData.grade11Courses,
      ...academicFormData.grade10Courses
    ];
    
    // Filter courses that have both a course code and a grade value
    const coursesWithGrades = allCourses.filter(course => 
      course.courseCode && course.grade
    );
    
    // Check if user has at least 6 courses with grades across any grade level
    return coursesWithGrades.length >= 6;
  };

  const handleSave = async () => {
    // Validate that user has at least 6 courses with grades in any one grade
    if (!validateMinimumCourses()) {
      setError('Please provide actual or projected grades for at least 6 courses across any grade level.');
      return;
    }
    
    // Clear any existing error
    setError(null);
    
    // Save both profile and academic data
    if (authUser?.uid) {
      try {
        const userRef = ref(database, `user/${authUser.uid}`);
        const calculatedClassYear = calculateClassYearFromGrade(formData.grade);
        
        // Determine the high school to save
        const highSchoolToSave = formData.highSchoolNotListed ? 'Fosterway High School' : formData.highSchool;
        const highSchoolIDToSave = formData.highSchoolNotListed ? '' : formData.highSchoolID;
        
        // Save profile information
        const profileUpdates: any = {
          'ProfileData/First Name': formData.firstName,
          'ProfileData/Last Name': formData.lastName,
          'ProfileData/Province': formData.province,
          'ProfileData/High School': highSchoolToSave,
          'ProfileData/hsID': highSchoolIDToSave,
          'ProfileData/Class Year': calculatedClassYear,
          'ProfileData/isStudent': formData.userType,
          'ProfileData/selfClaimedGuidance': formData.selfClaimedGuidance
        };
        
        // Save academic data if user has entered any courses
        const userGrade = formData.grade;
        
        // Get all courses from all grade levels to create a complete list
        const allCourses = [
          ...academicFormData.grade12Courses,
          ...academicFormData.grade11Courses,
          ...academicFormData.grade10Courses
        ];
        
        // Create a new grades object with only the courses that are currently in the local state
        const courseUpdates: any = {};
        
        allCourses.forEach((course) => {
          if (course.courseCode && course.grade) {
            // Determine the grade level based on the course code
            let gradeLevel = '';
            if (course.courseCode.includes('4U') || course.courseCode.includes('4M') || course.courseCode.includes('4C') || course.courseCode.includes('4E')) {
              gradeLevel = '12';
            } else if (course.courseCode.includes('3U') || course.courseCode.includes('3M') || course.courseCode.includes('3C') || course.courseCode.includes('3E')) {
              gradeLevel = '11';
            } else if (course.courseCode.includes('2D') || course.courseCode.includes('2P') || course.courseCode.includes('2L')) {
              gradeLevel = '10';
            } else {
              // Fallback to the user's current grade
              gradeLevel = userGrade.toString();
            }
            
            courseUpdates[course.courseCode] = {
              grade: gradeLevel, // Use the grade level (12, 11, 10)
              grades: parseFloat(course.grade) || 0,
              gradeType: course.gradeType // Store grade type separately
            };
          }
        });
        
        // Combine profile and academic updates
        const updates: any = {
          ...profileUpdates,
          grades: courseUpdates
        };
        
        await update(userRef, updates);
        
        // Log the grade addition activity
        if (authUser?.uid) {
          try {
            // Count total courses with grades
            const allCourses = [
              ...academicFormData.grade12Courses,
              ...academicFormData.grade11Courses,
              ...academicFormData.grade10Courses
            ];
            const coursesWithGrades = allCourses.filter(course => course.courseCode && course.grade).length;
            
            await activityLogService.logGradeAddition(authUser.uid, coursesWithGrades);
          } catch (error) {
            console.error('Error logging grade addition activity:', error);
            // Don't block the flow if logging fails
          }
        }

        // ProgramsContext will automatically detect user data changes and trigger calculation
        console.log('✅ OnboardingModal: User data updated - ProgramsContext will handle eligibility calculation');
      } catch (err: any) {
        console.error('Error saving data:', err);
        // Don't show error to user since this is optional data
      }
    }
    
    // Close modal and conditionally navigate to profile page
    onClose();
    if (redirectOnSave) {
      navigate('/my-student-profile');
    }
  };

  const provinces = [
    'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 
    'Newfoundland and Labrador', 'Northwest Territories', 'Nova Scotia', 
    'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 
    'Saskatchewan', 'Yukon'
  ];



  // Function to determine if modal can be closed
  const canCloseModal = () => {
    // If on Academic Info tab, check if user has minimum courses
    if (activeTab === 1) {
      return validateMinimumCourses();
    }
    // On Profile Info tab, prevent closing to ensure user completes profile first
    // This prevents closing after saving profile but before adding courses
    return false;
  };

  return (
    <Dialog 
      open={open} 
      onClose={canCloseModal() ? onClose : undefined}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ p: 0 }}>
        {/* Header with Background */}
        <Box sx={{
          backgroundColor: '#7fc400',
          backgroundImage: `url(${headerBg})`,
          backgroundRepeat: 'repeat',
          backgroundSize: 'auto',
          py: 3,
          px: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}>
          {/* Fosterway Logo */}
          <img 
            src="/static/media/foster-logo.d739e6dc347d086cdddb.png" 
            alt="Fosterway Logo" 
            style={{ 
              height: '40px', 
              width: 'auto'
            }} 
          />
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, px: 3, pb: 2, pt: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="onboarding tabs">
              <Tab 
                label="Profile Info" 
                sx={{ 
                  backgroundColor: activeTab === 0 ? 'primary.main' : 'grey.200',
                  color: activeTab === 0 ? 'white' : 'text.primary',
                  borderRadius: '4px 4px 0 0',
                  minWidth: 150,
                  '&.Mui-selected': {
                    color: 'white'
                  }
                }}
              />
              <Tab 
                label="Academic Info" 
                sx={{ 
                  backgroundColor: activeTab === 1 ? 'primary.main' : 'grey.200',
                  color: activeTab === 1 ? 'white' : 'text.primary',
                  borderRadius: '4px 4px 0 0',
                  minWidth: 150,
                  '&.Mui-selected': {
                    color: 'white'
                  }
                }}
              />
            </Tabs>
          </Box>
      </DialogTitle>

      <DialogContent>
        {/* Welcome Message - Now scrollable */}
        <Box sx={{ 
          px: { xs: 0, sm: 3 }, 
          py: { xs: 2, sm: 3 }, 
          pb: { xs: 1, sm: 2 },
          mb: { xs: 1, sm: 2 }
        }}>
          <Typography 
            variant={isMobile ? "body2" : "body1"}
            sx={{ 
              textAlign: 'justify',
              lineHeight: isMobile ? 1.4 : 1.6,
              color: 'text.secondary',
              fontSize: isMobile ? '0.875rem' : undefined
            }}
          >
            {activeTab === 0 
              ? "Welcome! Our goal at Fosterway is to simplify the research process for you to find the school that's right for you! While we require some basic information to get started, you'll have the ability to update all of this information later."
              : "By providing actual or anticipated grades for at least 6 courses across any grade level, we are able to customize program eligibility for you. Please enter grades for 6 or more courses below to get started."
            }
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>I am a...</InputLabel>
                <Select
                  value={formData.userType}
                  label="I am a..."
                  onChange={(e) => handleInputChange('userType', e.target.value as number)}
                >
                  <MenuItem value={1}>Student</MenuItem>
                  <MenuItem value={2}>Parent</MenuItem>
                  <MenuItem value={3}>Guidance</MenuItem>
                  <MenuItem value={4}>Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Province</InputLabel>
                <Select
                  value={formData.province}
                  label="Province"
                  onChange={(e) => handleInputChange('province', e.target.value)}
                >
                  {provinces.map((province) => (
                    <MenuItem key={province} value={province}>{province}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Grade</InputLabel>
                <Select
                  value={formData.grade}
                  label="Grade"
                  onChange={(e) => {
                    const gradeValue = parseInt(e.target.value.toString());
                    if (!isNaN(gradeValue)) {
                      handleGradeChange(gradeValue);
                    }
                  }}
                >
                  <MenuItem value={12}>Grade 12</MenuItem>
                  <MenuItem value={11}>Grade 11</MenuItem>
                  <MenuItem value={10}>Grade 10</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={highSchools}
                getOptionLabel={(option) => option.name}
                value={highSchools.find(school => school.name === formData.highSchool) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    handleHighSchoolChange(newValue.name);
                  } else {
                    handleHighSchoolChange('');
                  }
                }}
                disabled={loadingSchools || formData.highSchoolNotListed}
                loading={loadingSchools}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="High School Attended"
                    placeholder="Type to search for your school..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSchools ? 'Loading...' : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">
                        {option.name}
                      </Typography>
                      {option.city && (
                        <Typography variant="caption" color="text.secondary">
                          {option.city}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options;
                  
                  const filtered = options.filter(option =>
                    option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (option.city && option.city.toLowerCase().includes(inputValue.toLowerCase()))
                  );
                  
                  // If no matches found, return empty array
                  return filtered;
                }}
                isOptionEqualToValue={(option, value) => option.name === value?.name}
                noOptionsText="No schools found"
                clearOnEscape
                selectOnFocus
                handleHomeEndKeys
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.highSchoolNotListed}
                    onChange={(e) => handleInputChange('highSchoolNotListed', e.target.checked)}
                  />
                }
                label="My High School is not listed"
              />
            </Grid>

            {formData.highSchoolNotListed && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Add My High School"
                  value={formData.customHighSchool}
                  onChange={(e) => handleInputChange('customHighSchool', e.target.value)}
                  required={formData.highSchoolNotListed}
                />
              </Grid>
            )}

            {formData.userType === 3 && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.selfClaimedGuidance}
                      onChange={(e) => handleInputChange('selfClaimedGuidance', e.target.checked)}
                      required
                    />
                  }
                  label="I certify that I am in a Guidance role at the selected high school and authorize you to vet my information for additional access."
                />
              </Grid>
            )}


          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {(() => {
            const gradeInfo = getUserGradeCourses();
            
            return (
              <Box>
                {/* Course Entry Section */}
                <Box sx={{ mb: 3, mt: 0.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    justifyContent: { xs: 'flex-start', sm: 'space-between' }, 
                    mb: 2,
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {gradeInfo.title}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant={editingGradeLevel === 12 ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setEditingGradeLevel(12)}
                      >
                        Grade 12
                      </Button>
                      <Button
                        variant={editingGradeLevel === 11 ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setEditingGradeLevel(11)}
                      >
                        Grade 11
                      </Button>
                      <Button
                        variant={editingGradeLevel === 10 ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setEditingGradeLevel(10)}
                      >
                        Grade 10
                      </Button>
                      <Tooltip title="Add Course">
                        <IconButton onClick={() => addCourse(editingGradeLevel)} color="primary">
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  {editingGradeLevel === 12 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      To calculate your projected admissible programs, you need to tell us your real (or anticipated) grades in the Grade 12 courses you've completed or expect to complete. If you have more than 6 Grade 12 courses, click the + sign to add more rows for more courses.
                    </Typography>
                  )}
                  
                  {editingGradeLevel === 11 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Enter your real (or anticipated) grade from Grade 11 English (ENG3U). This course is a prerequisite for some programs. Optionally, you can also add other Grade 11 courses, click the + sign to add more rows for more courses.
                    </Typography>
                  )}
                  
                  {editingGradeLevel === 10 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Enter your Grade 10 courses and grades. Click the + sign to add more courses.
                    </Typography>
                  )}
                  
                  {/* Course Entries */}
                  {isMobile ? (
                    // Mobile: Vertical stacked cards
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: 2, 
                      pb: 2
                    }}>
                      {gradeInfo.courses.map((course, index) => (
                        <Box 
                          key={course.id} 
                          sx={{ 
                            width: '100%',
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            backgroundColor: 'background.paper',
                            boxShadow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2
                          }}
                        >
                          <Autocomplete
                            fullWidth
                            size="small"
                            options={courses.filter(c => c.grade === editingGradeLevel.toString())}
                            getOptionLabel={(option) => `${option.code} - ${option.name}`}
                            value={courses.find(c => c.code === course.courseCode) || null}
                            onChange={(event, newValue) => {
                              updateCourse(editingGradeLevel, course.id, 'courseCode', newValue?.code || '');
                            }}
                            disabled={loadingCourses}
                            loading={loadingCourses}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Course"
                                placeholder="Type to search for a course..."
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {loadingCourses ? 'Loading...' : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            renderOption={(props, option) => (
                              <Box component="li" {...props}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {option.code}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.name}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                            filterOptions={(options, { inputValue }) => {
                              if (!inputValue) return options;
                              
                              const filtered = options.filter(option =>
                                option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                                option.code.toLowerCase().includes(inputValue.toLowerCase())
                              );
                              
                              return filtered;
                            }}
                            isOptionEqualToValue={(option, value) => option.code === value?.code}
                            noOptionsText="No courses found"
                            clearOnEscape
                            selectOnFocus
                            handleHomeEndKeys
                          />
                          
                          <FormControl size="small" fullWidth>
                            <InputLabel>Grade Type</InputLabel>
                            <Select
                              value={course.gradeType}
                              label="Grade Type"
                              onChange={(e) => updateCourse(editingGradeLevel, course.id, 'gradeType', e.target.value as 'actual' | 'projected')}
                            >
                              <MenuItem value="actual">Actual</MenuItem>
                              <MenuItem value="projected">Projected</MenuItem>
                            </Select>
                          </FormControl>
                          
                          <TextField
                            size="small"
                            label="Grade %"
                            value={course.grade}
                            onChange={(e) => updateCourse(editingGradeLevel, course.id, 'grade', e.target.value)}
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, max: 100 }}
                          />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => removeCourse(editingGradeLevel, course.id)}
                              sx={{ color: 'error.main' }}
                            >
                              🗑️
                            </IconButton>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    // Desktop: Vertical table layout
                    gradeInfo.courses.map((course, index) => (
                      <Box key={course.id} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 200, flex: 1 }}
                          options={courses.filter(c => c.grade === editingGradeLevel.toString())}
                          getOptionLabel={(option) => `${option.code} - ${option.name}`}
                          value={courses.find(c => c.code === course.courseCode) || null}
                          onChange={(event, newValue) => {
                            updateCourse(editingGradeLevel, course.id, 'courseCode', newValue?.code || '');
                          }}
                          disabled={loadingCourses}
                          loading={loadingCourses}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Course"
                              placeholder="Type to search for a course..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loadingCourses ? 'Loading...' : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {option.code}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {option.name}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            
                            const filtered = options.filter(option =>
                              option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                              option.code.toLowerCase().includes(inputValue.toLowerCase())
                            );
                            
                            return filtered;
                          }}
                          isOptionEqualToValue={(option, value) => option.code === value?.code}
                          noOptionsText="No courses found"
                          clearOnEscape
                          selectOnFocus
                          handleHomeEndKeys
                        />
                        
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Grade Type</InputLabel>
                          <Select
                            value={course.gradeType}
                            label="Grade Type"
                            onChange={(e) => updateCourse(editingGradeLevel, course.id, 'gradeType', e.target.value as 'actual' | 'projected')}
                          >
                            <MenuItem value="actual">Actual</MenuItem>
                            <MenuItem value="projected">Projected</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <TextField
                          size="small"
                          label="Grade %"
                          value={course.grade}
                          onChange={(e) => updateCourse(editingGradeLevel, course.id, 'grade', e.target.value)}
                          sx={{ minWidth: 100 }}
                          type="number"
                          inputProps={{ min: 0, max: 100 }}
                        />
                        
                        <IconButton
                          size="small"
                          onClick={() => removeCourse(editingGradeLevel, course.id)}
                          sx={{ color: 'error.main' }}
                        >
                          🗑️
                        </IconButton>
                      </Box>
                    ))
                  )}
                  
                  {gradeInfo.courses.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Loading courses...
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })()}
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {/* Note for guidance users and parents */}
        {(formData.userType === 2 || formData.userType === 3) && !isGuidanceNoteDismissed && (
          <Box sx={{ 
            width: '100%', 
            mb: 2, 
            p: 2, 
            backgroundColor: 'info.light', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'info.main',
            position: 'relative'
          }}>
            <IconButton
              onClick={() => setIsGuidanceNoteDismissed(true)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="body1" sx={{ color: 'white', textAlign: 'center', pr: 3 }}>
              Please add grades for at least 6 classes on the next tab to view the features and functionality as a student would see it. You will have the ability to update this information in your profile at any time.
            </Typography>
          </Box>
        )}
        
        {activeTab === 1 && (
          <Button onClick={handleBack} disabled={isSaving}>
            Back
          </Button>
        )}
        
        {activeTab === 0 ? (
          <Button 
            onClick={handleNext} 
            variant="contained" 
            color="primary"
            disabled={isSaving}
            sx={{ ml: 'auto' }}
          >
            {isSaving ? 'Saving...' : 'Next'}
          </Button>
        ) : (
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            sx={{ ml: 'auto' }}
          >
            Complete
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingModal;
